import { useTranslation } from 'react-i18next'
import { type Discipline, gradesForDiscipline } from '@structural-climbing/validation'
import { useGymGradeConfig } from '../../layouts/useGymGradeConfig'
import { trimToUsedGrades } from './usedGradeRange'

interface GradeStat {
  grade: number
  count: number
}

interface Props {
  gradeStats: GradeStat[]
  loading?: boolean
  total?: number
  discipline?: Discipline
}

const BAR_AREA_PX = 120

/**
 * Active routes per grade as bars, the busiest grade drawn solid. Replaces the
 * pie: bars read in grade order, so a gap in the wall shows as a gap.
 */
export function RouteGradeChart({
  gradeStats,
  loading = false,
  total,
  discipline = 'boulder',
}: Props) {
  const { t } = useTranslation()
  const gradeConfig = useGymGradeConfig()

  // The gym Routes page filters the stats to the active discipline tab, so render
  // against that discipline's scale.
  const rows = trimToUsedGrades(
    gradesForDiscipline(gradeConfig, discipline).map((g) => ({
      grade: g.label,
      count: gradeStats.find((s) => s.grade === g.rank)?.count ?? 0,
    })),
    (r) => r.count > 0,
    discipline,
  )
  const max = Math.max(0, ...rows.map((r) => r.count))
  const isEmpty = max === 0

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-base font-semibold">{t('routes.chartTitle')}</p>
          <p className="text-xs text-foreground/50">{t('routes.chartSubtitle')}</p>
        </div>
        {total !== undefined && total > 0 && (
          <p className="shrink-0 text-sm font-semibold text-foreground/70">
            {t('routes.totalRoutes', { count: total })}
          </p>
        )}
      </div>
      {loading ? (
        <div
          className="animate-pulse rounded-lg bg-content2"
          style={{ height: BAR_AREA_PX + 24 }}
        />
      ) : isEmpty ? (
        <div className="flex items-center justify-center" style={{ height: BAR_AREA_PX + 24 }}>
          <p className="text-sm text-foreground/40">{t('routes.chartEmpty')}</p>
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          <div className="flex items-end gap-1.5" style={{ height: BAR_AREA_PX }}>
            {rows.map((r) => (
              <div
                key={r.grade}
                role="img"
                aria-label={`${r.grade}: ${t('routes.totalRoutes', { count: r.count })}`}
                title={`${r.grade}: ${t('routes.totalRoutes', { count: r.count })}`}
                className={`min-w-0 flex-1 rounded-t-md ${
                  r.count === max ? 'bg-primary' : 'bg-primary/15'
                }`}
                style={{ height: r.count > 0 ? `${Math.max(6, (r.count / max) * 100)}%` : 2 }}
              />
            ))}
          </div>
          <div className="flex gap-1.5">
            {rows.map((r) => (
              <span
                key={r.grade}
                className="min-w-0 flex-1 truncate text-center text-[11px] text-foreground/50"
              >
                {r.grade}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
